import { useEffect, useMemo, useState } from 'react';
import type { PairingPriority } from '@court-sync/shared';
import {
  CONFIGURABLE_PAIRING_PRIORITIES,
  buildConfigurablePairingOrder,
  normalizePairingPriorities,
  pairingPrioritiesFromOrder,
  type ConfigurablePairingPriority,
} from '@court-sync/shared';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Button, Checkbox, Field, FieldDescription, FieldLabel } from '@/components/ui';
import { PAIRING_PRIORITY_LABELS } from '@/lib/playEventLabels';

interface PlayEventPairingPrioritiesFieldProps {
  value: PairingPriority[];
  onChange: (value: PairingPriority[]) => void;
  disabled?: boolean;
}

export function PlayEventPairingPrioritiesField({
  value,
  onChange,
  disabled = false,
}: PlayEventPairingPrioritiesFieldProps) {
  const normalizedValue = useMemo(() => normalizePairingPriorities(value), [value]);
  const [order, setOrder] = useState<ConfigurablePairingPriority[]>(() =>
    buildConfigurablePairingOrder(normalizedValue)
  );

  useEffect(() => {
    setOrder(buildConfigurablePairingOrder(normalizedValue));
  }, [normalizedValue]);

  const enabled = useMemo(
    () => new Set<ConfigurablePairingPriority>(normalizedValue.filter((priority): priority is ConfigurablePairingPriority =>
      (CONFIGURABLE_PAIRING_PRIORITIES as readonly PairingPriority[]).includes(priority)
    )),
    [normalizedValue]
  );

  const emit = (
    nextOrder: ConfigurablePairingPriority[],
    nextEnabled: Set<ConfigurablePairingPriority>
  ) => {
    onChange(pairingPrioritiesFromOrder(nextOrder, nextEnabled));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    const nextOrder = [...order];
    [nextOrder[index], nextOrder[target]] = [nextOrder[target], nextOrder[index]];
    setOrder(nextOrder);
    emit(nextOrder, enabled);
  };

  const toggle = (priority: ConfigurablePairingPriority, checked: boolean) => {
    const nextEnabled = new Set(enabled);
    if (checked) {
      nextEnabled.add(priority);
    } else {
      if (nextEnabled.size <= 1) return;
      nextEnabled.delete(priority);
    }
    emit(order, nextEnabled);
  };

  return (
    <Field>
      <FieldLabel>Pairing priorities</FieldLabel>
      <FieldDescription>
        Higher items weigh more in matchups. Use the arrows to reorder; uncheck to ignore a rule.
      </FieldDescription>
      <ol className="mt-3 space-y-2">
        {order.map((priority, index) => {
          const isEnabled = enabled.has(priority);
          return (
            <li
              key={priority}
              className="flex items-center gap-2 rounded-lg border border-border/80 bg-muted/20 px-3 py-2"
            >
              <span className="w-5 shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                {index + 1}
              </span>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={disabled || index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Move ${PAIRING_PRIORITY_LABELS[priority]} up`}
                >
                  <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={disabled || index === order.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Move ${PAIRING_PRIORITY_LABELS[priority]} down`}
                >
                  <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
              <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                <Checkbox
                  checked={isEnabled}
                  disabled={disabled || (isEnabled && enabled.size <= 1)}
                  onCheckedChange={(checked) => toggle(priority, checked === true)}
                />
                <span className={isEnabled ? 'text-foreground' : 'text-muted-foreground'}>
                  {PAIRING_PRIORITY_LABELS[priority]}
                </span>
              </label>
            </li>
          );
        })}
      </ol>
    </Field>
  );
}
